import { type File, Memo } from '@kalos/kalos-rpc';
import { toast } from '@kalos/ui';
import { type OnChangeFn, type SortingState } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME,
  NOTE_REPORT_LIMIT,
  ROWS_PER_PAGE,
} from '../../../../../constants';
import { useAuth } from '../../../../../context/AuthContext';
import {
  useDeleteMemoMutation,
  useMemoClassificationQuery,
  useMemoQuery,
  useMemoStatusQuery,
  useUpdateMemoMutation,
} from '../../../../../hooks/react-query/useMemoQuery';
import { useBatchUserQuery } from '../../../../../hooks/react-query/useUserQuery';
import { debounce } from '../../../../../tools/helpers';
import { defaultJobNoteFilterValues, type JobNoteFilterValues } from './jobNoteFiltersUtils';
import { buildJobNoteMemoFilters, type MemoListOrderDir } from './jobNoteMemoRequest';

const CREATED_DATE_COLUMN_SORT_ID = 'created_date';

function memoListOrderFromSorting(sorting: SortingState): {
  orderBy: string;
  orderDir: MemoListOrderDir;
} {
  const first = sorting[0];
  if (first?.id === CREATED_DATE_COLUMN_SORT_ID) {
    return { orderBy: 'created_date', orderDir: first.desc ? 'DESC' : 'ASC' };
  }
  return { orderBy: 'created_date', orderDir: 'DESC' };
}

/**
 * A job's or a project's notes: the list query (filters, sort, page), the note-creator query
 * behind the filters, the statuses and categories, the add / edit / upload / delete / review
 * handlers and the AI report request. Moved verbatim out of `JobNoteList`, which renders the job
 * record's and today's project notes from it; the project record's desktop Notes tab uses it too.
 */
export const useJobNotes = ({
  jobId,
  projectPrimaryEventId,
}: {
  jobId?: number;
  projectPrimaryEventId?: number;
}) => {
  const [selectedNote, setSelectedNote] = useState<Memo | null>(null);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submissionLogTab, setSubmissionLogTab] = useState('Notes');
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [currentNoteIndex, setCurrentNoteIndex] = useState(-1);
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [sheetCurrentPage, setSheetCurrentPage] = useState(0);
  const [navigationDirection, setNavigationDirection] = useState<'next' | 'prev' | null>(null);
  const [sorting, setSorting] = useState<SortingState>([
    { id: CREATED_DATE_COLUMN_SORT_ID, desc: true },
  ]);
  const [filters, setFilters] = useState<JobNoteFilterValues>(defaultJobNoteFilterValues);

  // Create form in parent - this is the key change
  const filterForm = useForm<JobNoteFilterValues>({
    defaultValues: defaultJobNoteFilterValues,
  });

  // Set up debounced filter change in parent
  useEffect(() => {
    const debouncedUpdate = debounce((data: JobNoteFilterValues) => {
      setCurrentPage(0);
      setFilters(data);
    }, FORM_AUTOMATIC_SEARCH_DEBOUNCE_TIME);

    const subscription = filterForm.watch(debouncedUpdate);
    return () => subscription.unsubscribe();
  }, [filterForm]);

  const handleResetFilters = useCallback(() => {
    filterForm.reset(defaultJobNoteFilterValues);
    setFilters(defaultJobNoteFilterValues);
  }, [filterForm]);

  const memoSummaryParentDateRange = useMemo(
    () => ({
      startDate: filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : undefined,
      endDate: filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : undefined,
    }),
    [filters.startDate, filters.endDate],
  );

  const { user: loggedUser } = useAuth();
  const isAdmin = useMemo(
    () => loggedUser.permissionGroups.some((perm) => perm.name === 'Manager'),
    [loggedUser],
  );

  const isCanEditTranscript = useMemo(
    () => loggedUser.permissionGroups.some((perm) => perm.name === 'CanEditTranscript'),
    [loggedUser],
  );

  const canEditMemoStatus = useMemo(
    () =>
      loggedUser.permissionGroups.some(
        (perm) => perm.name === 'MemoAdmin' || perm.name === 'Manager',
      ),
    [loggedUser],
  );

  // Query to get all note creators for this job (for filter dropdown)
  const noteCreatorsQuery = useMemoQuery({
    filters: {
      ...(projectPrimaryEventId ? { projectPrimaryEventId } : { eventId: jobId }),
      isActive: true,
      withoutLimit: true,
      includeFiles: false,
    },
    enabled: !!(jobId || projectPrimaryEventId),
  });

  // Get unique creator IDs
  const noteCreatorIds = useMemo(() => {
    if (!noteCreatorsQuery.data?.results) return [];
    const uniqueIds = new Set(
      noteCreatorsQuery.data.results.map((note) => note.createdBy).filter((id) => id > 0),
    );
    return Array.from(uniqueIds);
  }, [noteCreatorsQuery.data?.results]);

  // Get unique event IDs (for project context job filter)
  const noteEventIds = useMemo(() => {
    if (!noteCreatorsQuery.data?.results) return [];
    const uniqueIds = new Set(
      noteCreatorsQuery.data.results.map((note) => note.eventId).filter((id) => id > 0),
    );
    return Array.from(uniqueIds).sort((a, b) => a - b);
  }, [noteCreatorsQuery.data?.results]);

  const memoListOrder = useMemo(() => memoListOrderFromSorting(sorting), [sorting]);

  const handleMemoSortingChange = useCallback<OnChangeFn<SortingState>>((updater) => {
    setSorting(updater);
    setCurrentPage(0);
  }, []);

  const noteQuery = useMemoQuery({
    enabled: !!(jobId || projectPrimaryEventId),
    filters: buildJobNoteMemoFilters({
      filters,
      jobId,
      projectPrimaryEventId,
      pageNumber: currentPage,
      withoutLimit: false,
      orderBy: memoListOrder.orderBy,
      orderDir: memoListOrder.orderDir,
    }),
  });

  const { mutateAsync: deleteMemo } = useDeleteMemoMutation();
  const { mutateAsync: updateMemo, isPending: isUpdatingMemo } = useUpdateMemoMutation();

  // Queries for detail sheet
  const userBatchGetQuery = useBatchUserQuery({
    filters: {
      isEmployee: 1,
      overrideLimit: true,
    },
  });
  const memoStatusQuery = useMemoStatusQuery();
  const memoClassificationQuery = useMemoClassificationQuery();

  const handleUploadFiles = useCallback((note: Memo) => {
    setSelectedNote(note);
    setShowUploadModal(true);
  }, []);

  const handleDeleteNote = useCallback(async () => {
    if (!selectedNote) return;

    try {
      await deleteMemo(selectedNote);
      setShowDeleteModal(false);
      setSelectedNote(null);
      toast({
        title: 'Note deleted',
        description: 'The note has been successfully deleted',
      });
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete the note',
        variant: 'destructive',
      });
    }
  }, [selectedNote, deleteMemo]);

  const closeFilesModal = useCallback(() => {
    setShowFilesModal(false);
    setSelectedNote(null);
  }, []);

  const closeUploadModal = useCallback(() => {
    setShowUploadModal(false);
    setSelectedNote(null);
  }, []);

  const handleEditNote = useCallback((note: Memo) => {
    setSelectedNote(note);
    setShowEditModal(true);
  }, []);

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    setSelectedNote(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedNote(null);
  }, []);

  const handleViewDetails = useCallback(
    (note: Memo, index: number) => {
      setSelectedNote(note);
      setCurrentNoteIndex(index);
      setSheetCurrentPage(currentPage);
      setShowDetailSheet(true);
    },
    [currentPage],
  );

  const closeDetailSheet = useCallback(() => {
    setShowDetailSheet(false);
    setSelectedNote(null);
    setCurrentNoteIndex(-1);
    setIsEditingMemo(false);
    setIsLoadingNextPage(false);
    setNavigationDirection(null);
  }, []);

  const getFileTypes = useCallback((files: File[] | undefined) => {
    if (!files || files.length === 0) return { hasAudio: false, hasPhoto: false, hasVideo: false };
    let hasAudio = false;
    let hasPhoto = false;
    let hasVideo = false;

    files.forEach((file) => {
      if (file.mimeType.includes('audio')) {
        hasAudio = true;
      }
      if (file.mimeType.includes('image') || file.mimeType.includes('pdf')) {
        hasPhoto = true;
      }
      if (file.mimeType.includes('video')) {
        hasVideo = true;
      }
    });

    return { hasAudio, hasPhoto, hasVideo };
  }, []);

  const handleSaveMemoInSheet = useCallback(
    async (updatedMemo?: Memo) => {
      const memoReq = updatedMemo
        ? Memo.clone(updatedMemo)
        : selectedNote
          ? Memo.clone(selectedNote)
          : null;

      if (!memoReq || !memoReq.fieldMask || memoReq.fieldMask.length === 0) {
        setIsEditingMemo(false);
        return;
      }

      try {
        await updateMemo(memoReq);

        // Update the selected note with the new data
        if (updatedMemo) {
          setSelectedNote(updatedMemo);
        }

        toast({
          title: 'Note updated',
          description: 'Your changes have been saved.',
        });
        setIsEditingMemo(false);
      } catch (err) {
        console.error('Error saving memo:', err);
        toast({
          title: 'Error',
          description: 'Failed to save changes',
          variant: 'destructive',
        });
      }
    },
    [selectedNote, updateMemo],
  );

  const handleSetState = useCallback((partialState: { selectedMemo: Memo }) => {
    setSelectedNote(partialState.selectedMemo);
  }, []);

  // Add effect to handle modal state changes
  useEffect(() => {
    const handleResize = () => {
      if (!selectedNote?.files?.results?.length) {
        setShowFilesModal(false);
        setSelectedNote(null);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedNote?.files?.results?.length]);

  // Pagination constants and calculations - use server totalCount
  const displayNotes = useMemo(() => {
    const notes = noteQuery.data?.results || [];
    return [...notes].sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return 0;
    });
  }, [noteQuery.data?.results]);

  const hasComments = useCallback(
    (memo: Memo) => Boolean(memo.commentCount && memo.commentCount > 0),
    [],
  );

  const totalNotes = noteQuery.data?.totalCount || 0;

  const [isViewingSavedReport, setIsViewingSavedReport] = useState(false);

  const noteFormJobId = useMemo(() => {
    if (jobId != null && jobId > 0) return jobId;
    if (projectPrimaryEventId != null && projectPrimaryEventId > 0) {
      return filters.eventId > 0 ? filters.eventId : projectPrimaryEventId;
    }
    return undefined;
  }, [jobId, projectPrimaryEventId, filters.eventId]);

  const reportMemoRequest = useMemo(
    () =>
      Memo.create(
        buildJobNoteMemoFilters({
          filters,
          jobId,
          projectPrimaryEventId,
          pageNumber: 0,
          withoutLimit: true,
          orderBy: memoListOrder.orderBy,
          orderDir: memoListOrder.orderDir,
        }),
      ),
    [filters, jobId, projectPrimaryEventId, memoListOrder.orderBy, memoListOrder.orderDir],
  );

  const isGenerateReportDisabled =
    noteQuery.isPending || totalNotes === 0 || totalNotes > NOTE_REPORT_LIMIT;

  const pageCount = Math.ceil(totalNotes / ROWS_PER_PAGE);
  const shouldShowPagination = totalNotes > ROWS_PER_PAGE;

  // Clamp currentPage when it falls out of bounds (e.g. after deleting the last
  // item on the last page). This follows the standard pagination best practice of
  // navigating to the nearest valid page rather than showing an empty page.
  useEffect(() => {
    if (!noteQuery.isPending && pageCount > 0 && currentPage >= pageCount) {
      setCurrentPage(pageCount - 1);
    }
  }, [currentPage, pageCount, noteQuery.isPending]);

  useEffect(() => {
    if (!isLoadingNextPage) {
      setSheetCurrentPage(currentPage);
    }
  }, [currentPage, isLoadingNextPage]);

  useEffect(() => {
    if (
      !isLoadingNextPage ||
      !navigationDirection ||
      noteQuery.isFetching ||
      !displayNotes.length
    ) {
      return;
    }

    const targetMemo =
      navigationDirection === 'next' ? displayNotes[0] : displayNotes[displayNotes.length - 1];
    const targetIndex = navigationDirection === 'next' ? 0 : displayNotes.length - 1;

    setSelectedNote(targetMemo);
    setCurrentNoteIndex(targetIndex);
    setIsEditingMemo(false);
    setIsLoadingNextPage(false);
    setNavigationDirection(null);
  }, [displayNotes, isLoadingNextPage, navigationDirection, noteQuery.isFetching]);

  const navigateToNote = useCallback(
    (direction: 'next' | 'prev') => {
      if (!displayNotes.length || !noteQuery.data) return;

      const currentIndex = currentNoteIndex;
      const page = sheetCurrentPage;
      const totalCount = noteQuery.data.totalCount || 0;
      const pageSize = ROWS_PER_PAGE;

      let newIndex: number;
      let newPage: number;
      let shouldLoadNewPage = false;

      if (direction === 'next') {
        if (currentIndex < displayNotes.length - 1) {
          newIndex = currentIndex + 1;
          newPage = page;
        } else {
          const currentGlobalIndex = page * pageSize + currentIndex;
          if (currentGlobalIndex < totalCount - 1) {
            newPage = page + 1;
            newIndex = 0;
            shouldLoadNewPage = true;
          } else {
            return;
          }
        }
      } else {
        if (currentIndex > 0) {
          newIndex = currentIndex - 1;
          newPage = page;
        } else if (page > 0) {
          newPage = page - 1;
          newIndex = pageSize - 1;
          shouldLoadNewPage = true;
        } else {
          return;
        }
      }

      if (shouldLoadNewPage) {
        setIsLoadingNextPage(true);
        setNavigationDirection(direction);
        setCurrentPage(newPage);
      } else {
        const newNote = displayNotes[newIndex];
        setSelectedNote(newNote);
        setCurrentNoteIndex(newIndex);
        setIsEditingMemo(false);
      }
    },
    [currentNoteIndex, displayNotes, noteQuery.data, sheetCurrentPage],
  );

  const canNavigateNext = useMemo(() => {
    if (!noteQuery.data?.results) return false;

    const pageSize = ROWS_PER_PAGE;
    const currentGlobalIndex = sheetCurrentPage * pageSize + currentNoteIndex;
    const totalCount = noteQuery.data.totalCount || 0;
    return currentGlobalIndex < totalCount - 1;
  }, [currentNoteIndex, noteQuery.data?.results, noteQuery.data?.totalCount, sheetCurrentPage]);

  const canNavigatePrev = useMemo(() => {
    return sheetCurrentPage > 0 || currentNoteIndex > 0;
  }, [currentNoteIndex, sheetCurrentPage]);

  return {
    selectedNote,
    setSelectedNote,
    showFilesModal,
    showUploadModal,
    showDeleteModal,
    setShowDeleteModal,
    submissionLogTab,
    setSubmissionLogTab,
    isReportDialogOpen,
    setIsReportDialogOpen,
    isAddNoteModalOpen,
    setIsAddNoteModalOpen,
    showEditModal,
    showDetailSheet,
    currentNoteIndex,
    isEditingMemo,
    setIsEditingMemo,
    currentPage,
    setCurrentPage,
    isLoadingNextPage,
    sheetCurrentPage,
    sorting,
    filters,
    filterForm,
    handleResetFilters,
    memoSummaryParentDateRange,
    loggedUser,
    isAdmin,
    isCanEditTranscript,
    canEditMemoStatus,
    noteCreatorsQuery,
    noteCreatorIds,
    noteEventIds,
    memoListOrder,
    handleMemoSortingChange,
    noteQuery,
    deleteMemo,
    updateMemo,
    isUpdatingMemo,
    userBatchGetQuery,
    memoStatusQuery,
    memoClassificationQuery,
    handleUploadFiles,
    handleDeleteNote,
    closeFilesModal,
    closeUploadModal,
    handleEditNote,
    handleEditSuccess,
    closeEditModal,
    handleViewDetails,
    closeDetailSheet,
    getFileTypes,
    handleSaveMemoInSheet,
    handleSetState,
    displayNotes,
    hasComments,
    totalNotes,
    isViewingSavedReport,
    setIsViewingSavedReport,
    noteFormJobId,
    reportMemoRequest,
    isGenerateReportDisabled,
    pageCount,
    shouldShowPagination,
    navigateToNote,
    canNavigateNext,
    canNavigatePrev,
  };
};
